/**
 * Edge type constraints: which node types can be source/target
 * '*' means any node type is allowed
 */

export interface EdgeTypeConstraint {
  sourceTypes: string[];
  targetTypes: string[];
}

export const EDGE_TYPE_CONSTRAINTS: Record<string, EdgeTypeConstraint> = {
  // Base CRM
  WORKS_AT: { sourceTypes: ['Person'], targetTypes: ['Organization'] },
  AFFILIATED_WITH: { sourceTypes: ['Person'], targetTypes: ['Organization'] },
  REPORTS_TO: { sourceTypes: ['Person'], targetTypes: ['Person'] },
  PARTICIPATED_IN: {
    sourceTypes: ['Person'],
    targetTypes: ['Activity', 'Deal', 'FundraisingOpportunity'],
  },
  RELATED_TO: {
    sourceTypes: [
      'Activity',
      'Task',
      'Document',
      'Opportunity',
      'Campaign',
      'Event',
      'Deal',
      'Investment',
      'ExternalFund',
      'Vehicle',
      'FundraisingOpportunity',
      'Commitment',
      'InvestmentAccount',
      'CapitalCall',
      'Distribution',
      'Report',
    ],
    targetTypes: ['*'],
  },

  // Sales
  OPPORTUNITY_FOR: {
    sourceTypes: ['Opportunity', 'FundraisingOpportunity'],
    targetTypes: ['Organization', 'Vehicle'],
  },
  CONTACT_FOR: {
    sourceTypes: ['Person'],
    targetTypes: ['Opportunity', 'Commitment', 'InvestmentAccount'],
  },
  ASSIGNED_TO: { sourceTypes: ['Opportunity', 'Deal', 'Task'], targetTypes: ['Person'] },

  // Marketing
  MEMBER_OF: { sourceTypes: ['Person'], targetTypes: ['Campaign'] },
  REGISTERED_FOR: { sourceTypes: ['Person'], targetTypes: ['Event'] },
  PART_OF: { sourceTypes: ['Event'], targetTypes: ['Campaign'] },

  // PE/VC Deal Flow
  DEAL_FOR: { sourceTypes: ['Deal'], targetTypes: ['Organization'] },
  INVESTING_FROM: { sourceTypes: ['Deal'], targetTypes: ['Vehicle'] },
  CONVERTED_TO: { sourceTypes: ['Deal'], targetTypes: ['Investment'] },

  // PE/VC Portfolio
  INVESTMENT_IN: { sourceTypes: ['Investment'], targetTypes: ['Organization', 'ExternalFund'] },
  INVESTMENT_FROM: { sourceTypes: ['Investment'], targetTypes: ['Vehicle'] },
  ORIGINATED_FROM: { sourceTypes: ['Investment'], targetTypes: ['Deal'] },
  MANAGED_BY: { sourceTypes: ['ExternalFund'], targetTypes: ['Organization', 'Person'] },
  HAS_PORTFOLIO: { sourceTypes: ['ExternalFund'], targetTypes: ['Organization'] },

  // PE/VC IR
  COMMITMENT_FROM: { sourceTypes: ['Commitment'], targetTypes: ['InvestmentAccount'] },
  COMMITMENT_TO: { sourceTypes: ['Commitment'], targetTypes: ['Vehicle'] },
  ACCOUNT_FOR: { sourceTypes: ['InvestmentAccount'], targetTypes: ['Organization'] },
  CALL_FOR: { sourceTypes: ['CapitalCall'], targetTypes: ['Commitment'] },
  CALL_FROM: { sourceTypes: ['CapitalCall'], targetTypes: ['Vehicle'] },
  DISTRIBUTION_FOR: { sourceTypes: ['Distribution'], targetTypes: ['Commitment'] },
  DISTRIBUTION_FROM: { sourceTypes: ['Distribution'], targetTypes: ['Vehicle'] },
  REPORT_FOR: { sourceTypes: ['Report'], targetTypes: ['Vehicle'] },
  SENT_TO: { sourceTypes: ['Report'], targetTypes: ['Commitment'] },

  // PMN (any node can have these)
  HAS_PROPERTY: { sourceTypes: ['*'], targetTypes: ['Property'] },
  HAS_METRIC: { sourceTypes: ['*'], targetTypes: ['Metric'] },
  HAS_NARRATIVE: { sourceTypes: ['*'], targetTypes: ['Narrative'] },
};

/**
 * Check if an edge type allows a given source node type
 */
export function isValidSourceType(edgeType: string, nodeType: string): boolean {
  const constraint = EDGE_TYPE_CONSTRAINTS[edgeType];
  if (!constraint) return true; // No constraint = allow
  return constraint.sourceTypes.includes('*') || constraint.sourceTypes.includes(nodeType);
}

/**
 * Check if an edge type allows a given target node type
 */
export function isValidTargetType(edgeType: string, nodeType: string): boolean {
  const constraint = EDGE_TYPE_CONSTRAINTS[edgeType];
  if (!constraint) return true; // No constraint = allow
  return constraint.targetTypes.includes('*') || constraint.targetTypes.includes(nodeType);
}
